// src/pages/bloomterminal/alerts/steps/CustomAlertCommodityConfig.jsx
import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import CommoditySelector from './AlertCommoditySelect';
import Dropdown from '../../../components/Dropdown';
import { useGetCommoditySkuPrice, useGetCommoditySkuVolume } from '../../../utils/api-hooks/alerts';
import FilterDropdown from '../../components/FilterDropdown';
import FilterModal from '../../components/FilterModal';
import ModalPortalSelect from '../components/ModalPortalSelect';

const INVALID_VALUES = ['N/A', 'n/a', 'NA', 'na', 'Not Classified', 'Not classified', 'null', 'NULL', ''];

const isWeatherRelatedAlertType = (alertType) => ['rain', 'temperature'].includes(alertType);

const toDropdownOptions = (items) => items.map((item) => ({ label: item, value: item }));

const ModalFilterTrigger = ({ label, value, onClick }) => (
  <div className="flex w-full flex-col gap-2">
    <label className="font-['Inter'] text-[14px] text-[#282A29]">{label}</label>
    <button
      type="button"
      onClick={onClick}
      className="flex h-[42px] w-full items-center justify-between rounded-[12px] border border-[#D5D8D6] bg-white px-4 font-['Inter'] text-[16px] text-[#282A29]"
    >
      <span className={`truncate ${value ? '' : 'text-[#828382]'}`}>{value || 'Select...'}</span>
      <ChevronDown size={16} className="shrink-0 text-[#A0A3A1]" />
    </button>
  </div>
);

const isInvalidOptionValue = (v) => {
  if (v == null || v === '') return true;
  const s = String(v).trim();
  if (!s) return true;
  if (INVALID_VALUES.includes(s)) return true;
  if (/^n\/a$/i.test(s)) return true;
  if (/^not\s*classified$/i.test(s)) return true;
  return false;
};

// FOB-like behavior:
// - Remove empty values
// - Only hide the dropdown when the sole option is an "invalid" placeholder (e.g. N/A)
const getStrictCleanOptions = (data, field) => {
  if (!data || data.length === 0) return [];
  const options = [
    ...new Set(
      data
        .map((d) => (d[field] != null ? String(d[field]).trim() : ''))
        .filter((v) => v)
    ),
  ].sort();

  if (options.length === 1 && isInvalidOptionValue(options[0])) return [];
  return options;
};

// Strict "valid-only" options for defaulting/cascade decisions (excludes N/A, Not Classified, etc.)
const getStrictValidOptions = (data, field) => {
  if (!data || data.length === 0) return [];
  const valid = data
    .map((d) => (d[field] != null ? String(d[field]).trim() : ''))
    .filter((v) => v && !isInvalidOptionValue(v));
  return [...new Set(valid)].sort();
};

const getMostCommonOption = (data, field) => {
  const counts = {};
  data.forEach((item) => {
    const raw = item[field];
    if (raw == null || raw === '') return;
    const value = String(raw).trim();
    if (!value || isInvalidOptionValue(value)) return;
    counts[value] = (counts[value] || 0) + 1;
  });

  let maxCount = 0;
  let mostCommon = '';
  Object.entries(counts).forEach(([value, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommon = value;
    }
  });
  return mostCommon;
};

const getMostCommonRawValue = (data, field) => {
  const counts = {};
  data.forEach((item) => {
    const raw = item[field];
    if (raw == null || raw === '') return;
    const value = String(raw).trim();
    if (!value) return;
    counts[value] = (counts[value] || 0) + 1;
  });

  let maxCount = 0;
  let mostCommon = '';
  Object.entries(counts).forEach(([value, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommon = value;
    }
  });
  return mostCommon;
};

const pickValidOrDefault = (data, field, preferred) => {
  const options = getStrictValidOptions(data, field);
  if (preferred && options.includes(preferred)) return preferred;
  const valid = getMostCommonOption(data, field);
  if (valid) return valid;

  // If there are no valid (non-N/A) options, fall back to the most common raw value
  // (often "N/A"). This keeps step validation/payloads consistent while the UI omits the
  // dropdown (because options=[]).
  return getMostCommonRawValue(data, field) || '';
};

const CustomAlertCommodityConfig = ({
  selectedAlertType,
  selectedCommodity,
  onCommodityToggle,
  skuSelection,
  onSkuChange,
  hideCommoditySelector = false,
  variant = 'default',
}) => {
  const { data: skuPriceData, isLoading: isLoadingSkuPrice } = useGetCommoditySkuPrice(
    selectedAlertType === 'price' ? selectedCommodity : null
  );

  const { data: skuVolumeData, isLoading: isLoadingSkuVolume } = useGetCommoditySkuVolume(
    selectedAlertType === 'volume' ? selectedCommodity : null
  );

  const isLoadingSku = isLoadingSkuPrice || isLoadingSkuVolume;
  const initializedCommodityRef = useRef(null);

  // Volume-by-Origin UI modals (multi-select)
  const [volumeTypeModalOpen, setVolumeTypeModalOpen] = useState(false);
  const [volumeVarietyModalOpen, setVolumeVarietyModalOpen] = useState(false);
  const [volumeOriginModalOpen, setVolumeOriginModalOpen] = useState(false);

  const calculatePriceCascade = useCallback((data, options = {}) => {
    const {
      district = null,
      type = null,
      variety = null,
      size = null,
      grade = null,
      // allow callers to pass the current selection so we can keep-if-valid
      currentSelection = null,
    } = options;
    let filtered = data;

    // District / Origin (FOB Price Pulse uses `district`; fallback to `origin` if needed)
    const preferredDistrict = district ?? currentSelection?.district ?? null;
    const districtField = filtered.some((d) => d?.district != null) ? 'district' : 'origin';
    const newDistrict = pickValidOrDefault(filtered, districtField, preferredDistrict);
    if (newDistrict) filtered = filtered.filter((item) => (item[districtField] ?? '') === newDistrict);

    // Type
    const preferredType = type ?? currentSelection?.type ?? null;
    const newType = pickValidOrDefault(filtered, 'type', preferredType);
    if (newType) filtered = filtered.filter((item) => item.type === newType);

    // Variety
    const preferredVariety = variety ?? currentSelection?.variety ?? null;
    const newVariety = pickValidOrDefault(filtered, 'variety', preferredVariety);
    if (newVariety) filtered = filtered.filter(item => item.variety === newVariety);

    // Size
    const preferredSize = size ?? currentSelection?.size ?? null;
    const newSize = pickValidOrDefault(filtered, 'item_size', preferredSize);
    if (newSize) filtered = filtered.filter(item => item.item_size === newSize);

    // Grade
    const preferredGrade = grade ?? currentSelection?.grade ?? null;
    const newGrade = pickValidOrDefault(filtered, 'grade', preferredGrade);
    if (newGrade) filtered = filtered.filter(item => item.grade === newGrade);

    // Package/SKU
    const preferredPackage = currentSelection?.package ?? null;
    const packageField = filtered.some((d) => d?.package != null) ? 'package' : 'sku';
    const newPackage = pickValidOrDefault(filtered, packageField, preferredPackage);
    if (newPackage) filtered = filtered.filter((item) => (item[packageField] ?? '') === newPackage);

    // Tail fields (match FOB Price Pulse): Storage → Condition → Environment → Quality → Appearance → Properties
    const newStorage = pickValidOrDefault(filtered, 'storage', currentSelection?.storage ?? null);
    if (newStorage) filtered = filtered.filter((item) => item.storage === newStorage);

    const newCondition = pickValidOrDefault(filtered, 'condition', currentSelection?.condition ?? null);
    if (newCondition) filtered = filtered.filter((item) => item.condition === newCondition);

    const newEnvironment = pickValidOrDefault(filtered, 'environment', currentSelection?.environment ?? null);
    if (newEnvironment) filtered = filtered.filter((item) => item.environment === newEnvironment);

    const newQuality = pickValidOrDefault(filtered, 'quality', currentSelection?.quality ?? null);
    if (newQuality) filtered = filtered.filter((item) => item.quality === newQuality);

    const newAppearance = pickValidOrDefault(filtered, 'appearance', currentSelection?.appearance ?? null);
    if (newAppearance) filtered = filtered.filter((item) => item.appearance === newAppearance);

    const newProperties = pickValidOrDefault(filtered, 'properties', currentSelection?.properties ?? null);

    return {
      district: newDistrict || '',
      type: newType || '',
      variety: newVariety || '',
      size: newSize || '',
      grade: newGrade || '',
      package: newPackage || '',
      storage: newStorage || '',
      condition: newCondition || '',
      environment: newEnvironment || '',
      quality: newQuality || '',
      appearance: newAppearance || '',
      properties: newProperties || '',

      // keep volume selections intact when editing price
      types: currentSelection?.types ?? [],
      varieties: currentSelection?.varieties ?? [],
      origins: currentSelection?.origins ?? [],
    };
  }, []);

  const calculateVolumeCascade = useCallback((data, options = {}) => {
    // Match Volume by Origin cascade:
    // Type (multi) → Variety (multi) → Origins (multi)
    const { types = null, varieties = null, origins = null, currentSelection = null } = options;
    let filtered = data;

    const allTypes = getStrictValidOptions(filtered, 'type');
    let nextTypes = Array.isArray(types) ? types : (currentSelection?.types ?? []);
    if (nextTypes.length === 0) {
      if (allTypes.includes('Conventional')) nextTypes = ['Conventional'];
      else if (allTypes.length > 0) nextTypes = [allTypes[0]];
    } else {
      nextTypes = nextTypes.filter((t) => allTypes.includes(t));
      if (nextTypes.length === 0 && allTypes.length > 0) nextTypes = [allTypes[0]];
    }
    filtered = filtered.filter((item) => item.type && nextTypes.includes(item.type));

    const allVarieties = getStrictValidOptions(filtered, 'variety');
    let nextVarieties = Array.isArray(varieties) ? varieties : (currentSelection?.varieties ?? []);
    if (nextVarieties.length === 0) {
      nextVarieties = [...allVarieties];
    } else {
      nextVarieties = nextVarieties.filter((v) => allVarieties.includes(v));
      if (nextVarieties.length === 0) nextVarieties = [...allVarieties];
    }
    if (nextVarieties.length > 0) {
      filtered = filtered.filter((item) => (item.variety ?? '') && nextVarieties.includes(item.variety));
    }

    const originField = filtered.some((d) => d?.origin != null) ? 'origin' : 'district';
    const allOrigins = getStrictValidOptions(filtered, originField);
    let nextOrigins = Array.isArray(origins) ? origins : (currentSelection?.origins ?? []);
    if (nextOrigins.length === 0) {
      nextOrigins = [...allOrigins];
    } else {
      nextOrigins = nextOrigins.filter((o) => allOrigins.includes(o));
      if (nextOrigins.length === 0) nextOrigins = [...allOrigins];
    }

    return {
      types: nextTypes,
      varieties: nextVarieties,
      origins: nextOrigins,

      // keep price selections intact when editing volume
      district: currentSelection?.district ?? '',
      type: currentSelection?.type ?? '',
      variety: currentSelection?.variety ?? '',
      size: currentSelection?.size ?? '',
      grade: currentSelection?.grade ?? '',
      package: currentSelection?.package ?? '',
      storage: currentSelection?.storage ?? '',
      condition: currentSelection?.condition ?? '',
      environment: currentSelection?.environment ?? '',
      quality: currentSelection?.quality ?? '',
      appearance: currentSelection?.appearance ?? '',
      properties: currentSelection?.properties ?? '',
    };
  }, []);

  useEffect(() => {
    if (selectedAlertType === 'price' && skuPriceData && skuPriceData.length > 0) {
      if (initializedCommodityRef.current !== selectedCommodity) {
        initializedCommodityRef.current = selectedCommodity;
        const cascade = calculatePriceCascade(skuPriceData, { currentSelection: skuSelection });
        onSkuChange(cascade);
      }
    } else if (selectedAlertType === 'volume' && skuVolumeData && skuVolumeData.length > 0) {
      if (initializedCommodityRef.current !== selectedCommodity) {
        initializedCommodityRef.current = selectedCommodity;
        const cascade = calculateVolumeCascade(skuVolumeData, { currentSelection: skuSelection });
        onSkuChange(cascade);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuPriceData, skuVolumeData, selectedAlertType, selectedCommodity]);

  const {
    districtOptions,
    varietyOptions,
    sizeOptions,
    gradeOptions,
    typeOptions,
    packageOptions,
    storageOptions,
    conditionOptions,
    environmentOptions,
    qualityOptions,
    appearanceOptions,
    propertiesOptions,

    volumeTypeOptions,
    volumeVarietyOptions,
    volumeOriginOptions,
  } = useMemo(() => {
    let districts = [];
    let varieties = [];
    let sizes = [];
    let grades = [];
    let types = [];
    let packages = [];
    let storages = [];
    let conditions = [];
    let environments = [];
    let qualities = [];
    let appearances = [];
    let properties = [];

    let vTypes = [];
    let vVarieties = [];
    let vOrigins = [];

    if (skuPriceData && selectedAlertType === 'price') {
      // Cascade options like FOB Price Pulse: each level is computed from the currently-filtered dataset.
      // Unlike the old Alerts implementation, N/A-only levels should NOT block downstream levels.
      let filtered = skuPriceData;

      const districtField = filtered.some((d) => d?.district != null) ? 'district' : 'origin';
      districts = getStrictCleanOptions(filtered, districtField);
      if (skuSelection.district && districts.includes(skuSelection.district)) {
        filtered = filtered.filter((item) => (item[districtField] ?? '') === skuSelection.district);
      }

      types = getStrictCleanOptions(filtered, 'type');
      if (skuSelection.type && types.includes(skuSelection.type)) {
        filtered = filtered.filter((item) => item.type === skuSelection.type);
      }

      varieties = getStrictCleanOptions(filtered, 'variety');
      if (skuSelection.variety && varieties.includes(skuSelection.variety)) {
        filtered = filtered.filter(item => item.variety === skuSelection.variety);
      }

      sizes = getStrictCleanOptions(filtered, 'item_size');
      if (skuSelection.size && sizes.includes(skuSelection.size)) {
        filtered = filtered.filter(item => item.item_size === skuSelection.size);
      }

      grades = getStrictCleanOptions(filtered, 'grade');
      if (skuSelection.grade && grades.includes(skuSelection.grade)) {
        filtered = filtered.filter(item => item.grade === skuSelection.grade);
      }

      const packageField = filtered.some((d) => d?.package != null) ? 'package' : 'sku';
      packages = getStrictCleanOptions(filtered, packageField);
      if (skuSelection.package && packages.includes(skuSelection.package)) {
        filtered = filtered.filter((item) => (item[packageField] ?? '') === skuSelection.package);
      }

      storages = getStrictCleanOptions(filtered, 'storage');
      if (skuSelection.storage && storages.includes(skuSelection.storage)) {
        filtered = filtered.filter((item) => item.storage === skuSelection.storage);
      }
      conditions = getStrictCleanOptions(filtered, 'condition');
      if (skuSelection.condition && conditions.includes(skuSelection.condition)) {
        filtered = filtered.filter((item) => item.condition === skuSelection.condition);
      }
      environments = getStrictCleanOptions(filtered, 'environment');
      if (skuSelection.environment && environments.includes(skuSelection.environment)) {
        filtered = filtered.filter((item) => item.environment === skuSelection.environment);
      }
      qualities = getStrictCleanOptions(filtered, 'quality');
      if (skuSelection.quality && qualities.includes(skuSelection.quality)) {
        filtered = filtered.filter((item) => item.quality === skuSelection.quality);
      }
      appearances = getStrictCleanOptions(filtered, 'appearance');
      if (skuSelection.appearance && appearances.includes(skuSelection.appearance)) {
        filtered = filtered.filter((item) => item.appearance === skuSelection.appearance);
      }
      properties = getStrictCleanOptions(filtered, 'properties');
    } else if (skuVolumeData && selectedAlertType === 'volume') {
      let filtered = skuVolumeData;

      vTypes = getStrictCleanOptions(filtered, 'type');
      if (Array.isArray(skuSelection.types) && skuSelection.types.length > 0) {
        filtered = filtered.filter((item) => item.type && skuSelection.types.includes(item.type));
      }

      vVarieties = getStrictCleanOptions(filtered, 'variety');
      if (Array.isArray(skuSelection.varieties) && skuSelection.varieties.length > 0) {
        filtered = filtered.filter((item) => item.variety && skuSelection.varieties.includes(item.variety));
      }

      const originField = filtered.some((d) => d?.origin != null) ? 'origin' : 'district';
      vOrigins = getStrictCleanOptions(filtered, originField);
    }

    return {
      districtOptions: toDropdownOptions(districts),
      varietyOptions: toDropdownOptions(varieties),
      sizeOptions: toDropdownOptions(sizes),
      gradeOptions: toDropdownOptions(grades),
      typeOptions: toDropdownOptions(types),
      packageOptions: toDropdownOptions(packages),

      storageOptions: toDropdownOptions(storages),
      conditionOptions: toDropdownOptions(conditions),
      environmentOptions: toDropdownOptions(environments),
      qualityOptions: toDropdownOptions(qualities),
      appearanceOptions: toDropdownOptions(appearances),
      propertiesOptions: toDropdownOptions(properties),

      volumeTypeOptions: vTypes,
      volumeVarietyOptions: vVarieties,
      volumeOriginOptions: vOrigins,
    };
  }, [skuPriceData, skuVolumeData, selectedAlertType, skuSelection]);

  const handleDistrictChange = useCallback((value) => {
    if (selectedAlertType === 'price' && skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, { district: value, currentSelection: skuSelection }));
    }
  }, [selectedAlertType, skuPriceData, calculatePriceCascade, onSkuChange, skuSelection]);

  const handleTypeChange = useCallback((value) => {
    if (skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, {
        district: skuSelection.district,
        type: value,
        currentSelection: skuSelection,
      }));
    }
  }, [skuPriceData, skuSelection.district, calculatePriceCascade, onSkuChange, skuSelection]);

  const handleVarietyChange = useCallback((value) => {
    if (selectedAlertType === 'price' && skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, {
        district: skuSelection.district,
        type: skuSelection.type,
        variety: value,
        currentSelection: skuSelection,
      }));
    }
  }, [selectedAlertType, skuPriceData, skuVolumeData, calculatePriceCascade, calculateVolumeCascade, onSkuChange, skuSelection]);

  const handleSizeChange = useCallback((value) => {
    if (skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, {
        district: skuSelection.district,
        type: skuSelection.type,
        variety: skuSelection.variety,
        size: value,
        currentSelection: skuSelection,
      }));
    }
  }, [skuPriceData, skuSelection.district, skuSelection.type, skuSelection.variety, calculatePriceCascade, onSkuChange, skuSelection]);

  const handleGradeChange = useCallback((value) => {
    if (skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, {
        district: skuSelection.district,
        type: skuSelection.type,
        variety: skuSelection.variety,
        size: skuSelection.size,
        grade: value,
        currentSelection: skuSelection,
      }));
    }
  }, [skuPriceData, skuSelection.district, skuSelection.type, skuSelection.variety, skuSelection.size, calculatePriceCascade, onSkuChange, skuSelection]);

  const handlePackageChange = useCallback((value) => {
    // keep other selections but ensure SKU/package is valid for current combination
    if (skuPriceData) {
      onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, package: value } }));
    } else {
      onSkuChange({ ...skuSelection, package: value });
    }
  }, [skuSelection, onSkuChange]);

  const handleStorageChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, storage: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);
  const handleConditionChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, condition: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);
  const handleEnvironmentChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, environment: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);
  const handleQualityChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, quality: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);
  const handleAppearanceChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, appearance: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);
  const handlePropertiesChange = useCallback((value) => {
    if (skuPriceData) onSkuChange(calculatePriceCascade(skuPriceData, { currentSelection: { ...skuSelection, properties: value } }));
  }, [skuPriceData, skuSelection, calculatePriceCascade, onSkuChange]);

  const handleCommodityChange = (commodity) => {
    initializedCommodityRef.current = null;
    onCommodityToggle(commodity);
    onSkuChange({
      district: '',
      type: '',
      variety: '',
      size: '',
      grade: '',
      package: '',
      storage: '',
      condition: '',
      environment: '',
      quality: '',
      appearance: '',
      properties: '',
      types: [],
      varieties: [],
      origins: [],
    });
  };

  const showSkuSelectors = selectedCommodity && !isWeatherRelatedAlertType(selectedAlertType);
  const isModal = variant === 'modal';
  const Field = isModal ? ModalPortalSelect : Dropdown;
  const fieldClassName = isModal ? 'w-full' : 'w-[280px]';

  return (
    <div className={`flex flex-col ${isModal ? 'gap-5' : 'gap-6'}`}>
      {!hideCommoditySelector && (
        <CommoditySelector
          selectedCommodity={selectedCommodity}
          onCommodityToggle={handleCommodityChange}
        />
      )}

      {showSkuSelectors && (
        <>
          {isLoadingSku ? (
            <div role="status" aria-busy="true" aria-label="Loading" className={isModal ? 'grid grid-cols-1 gap-4 sm:grid-cols-2' : 'flex flex-wrap gap-4'}>
              <div className="skeleton-sheen h-10 w-full min-w-[160px] rounded-[8px]" />
              <div className="skeleton-sheen h-10 w-full min-w-[160px] rounded-[8px]" />
            </div>
          ) : selectedAlertType === 'price' ? (
            <div className={isModal ? 'grid grid-cols-1 gap-4 sm:grid-cols-2' : 'flex flex-wrap gap-4'}>
              {districtOptions.length > 0 && (
                <Field
                  label="Origins (district)"
                  value={skuSelection.district}
                  placeholder="Select..."
                  options={districtOptions}
                  onSelect={handleDistrictChange}
                  className={fieldClassName}
                />
              )}

              {typeOptions.length > 0 && (
                <Field
                  label="Type"
                  value={skuSelection.type}
                  placeholder="Select..."
                  options={typeOptions}
                  onSelect={handleTypeChange}
                  className={fieldClassName}
                />
              )}

              {varietyOptions.length > 0 && (
                <Field
                  label="Variety"
                  value={skuSelection.variety}
                  placeholder="Select..."
                  options={varietyOptions}
                  onSelect={handleVarietyChange}
                  className={fieldClassName}
                />
              )}

              {sizeOptions.length > 0 && (
                <Field
                  label="Size"
                  value={skuSelection.size}
                  placeholder="Select..."
                  options={sizeOptions}
                  onSelect={handleSizeChange}
                  className={fieldClassName}
                />
              )}

              {gradeOptions.length > 0 && (
                <Field
                  label="Grade"
                  value={skuSelection.grade}
                  placeholder="Select..."
                  options={gradeOptions}
                  onSelect={handleGradeChange}
                  className={fieldClassName}
                />
              )}

              {packageOptions.length > 0 && (
                <Field
                  label="SKU"
                  value={skuSelection.package}
                  placeholder="Select..."
                  options={packageOptions}
                  onSelect={handlePackageChange}
                  className={fieldClassName}
                />
              )}

              {storageOptions.length > 0 && (
                <Field label="Storage" value={skuSelection.storage} placeholder="Select..." options={storageOptions} onSelect={handleStorageChange} className={fieldClassName} />
              )}
              {conditionOptions.length > 0 && (
                <Field label="Condition" value={skuSelection.condition} placeholder="Select..." options={conditionOptions} onSelect={handleConditionChange} className={fieldClassName} />
              )}
              {environmentOptions.length > 0 && (
                <Field label="Environment" value={skuSelection.environment} placeholder="Select..." options={environmentOptions} onSelect={handleEnvironmentChange} className={fieldClassName} />
              )}
              {qualityOptions.length > 0 && (
                <Field label="Quality" value={skuSelection.quality} placeholder="Select..." options={qualityOptions} onSelect={handleQualityChange} className={fieldClassName} />
              )}
              {appearanceOptions.length > 0 && (
                <Field label="Appearance" value={skuSelection.appearance} placeholder="Select..." options={appearanceOptions} onSelect={handleAppearanceChange} className={fieldClassName} />
              )}
              {propertiesOptions.length > 0 && (
                <Field label="Properties" value={skuSelection.properties} placeholder="Select..." options={propertiesOptions} onSelect={handlePropertiesChange} className={fieldClassName} />
              )}
            </div>
          ) : selectedAlertType === 'volume' ? (
            <div className="flex flex-col gap-3 w-full">
              <div className={isModal ? 'grid grid-cols-1 gap-4 sm:grid-cols-2' : 'grid grid-cols-1 sm:grid-cols-3 gap-3'}>
                {volumeTypeOptions.length > 0 && (
                  isModal ? (
                    <ModalFilterTrigger
                      label="Type (multi-select)"
                      value={
                        skuSelection.types?.length
                          ? (skuSelection.types.length === volumeTypeOptions.length ? 'All types' : `${skuSelection.types.length} selected`)
                          : ''
                      }
                      onClick={() => setVolumeTypeModalOpen(true)}
                    />
                  ) : (
                    <FilterDropdown
                      label="Type (multi-select)"
                      value={
                        skuSelection.types?.length
                          ? (skuSelection.types.length === volumeTypeOptions.length ? 'All types' : `${skuSelection.types.length} selected`)
                          : 'Select...'
                      }
                      onClick={() => setVolumeTypeModalOpen(true)}
                    />
                  )
                )}
                {volumeVarietyOptions.length > 0 && (
                  isModal ? (
                    <ModalFilterTrigger
                      label="Variety (multi-select)"
                      value={
                        skuSelection.varieties?.length
                          ? (skuSelection.varieties.length === volumeVarietyOptions.length ? 'All varieties' : `${skuSelection.varieties.length} selected`)
                          : ''
                      }
                      onClick={() => setVolumeVarietyModalOpen(true)}
                    />
                  ) : (
                    <FilterDropdown
                      label="Variety (multi-select)"
                      value={
                        skuSelection.varieties?.length
                          ? (skuSelection.varieties.length === volumeVarietyOptions.length ? 'All varieties' : `${skuSelection.varieties.length} selected`)
                          : 'Select...'
                      }
                      onClick={() => setVolumeVarietyModalOpen(true)}
                    />
                  )
                )}
                {volumeOriginOptions.length > 0 && (
                  isModal ? (
                    <ModalFilterTrigger
                      label="Origins (multi-select)"
                      value={
                        skuSelection.origins?.length
                          ? (skuSelection.origins.length === volumeOriginOptions.length ? 'All origins' : `${skuSelection.origins.length} selected`)
                          : ''
                      }
                      onClick={() => setVolumeOriginModalOpen(true)}
                    />
                  ) : (
                    <FilterDropdown
                      label="Origins (multi-select)"
                      value={
                        skuSelection.origins?.length
                          ? (skuSelection.origins.length === volumeOriginOptions.length ? 'All origins' : `${skuSelection.origins.length} selected`)
                          : 'Select...'
                      }
                      onClick={() => setVolumeOriginModalOpen(true)}
                    />
                  )
                )}
              </div>

              <FilterModal
                isOpen={volumeTypeModalOpen}
                onClose={() => {
                  setVolumeTypeModalOpen(false);
                  if (skuVolumeData) onSkuChange(calculateVolumeCascade(skuVolumeData, { currentSelection: skuSelection }));
                }}
                title="Select Types"
                options={volumeTypeOptions}
                selectedValues={skuSelection.types}
                onSelect={(v) => {
                  const next = (skuSelection.types || []).includes(v)
                    ? (skuSelection.types || []).filter((x) => x !== v)
                    : [...(skuSelection.types || []), v];
                  onSkuChange({ ...skuSelection, types: next });
                }}
                multiSelect
                onSelectAll={() => onSkuChange({ ...skuSelection, types: [...volumeTypeOptions] })}
                onClearAll={() => onSkuChange({ ...skuSelection, types: [] })}
              />

              <FilterModal
                isOpen={volumeVarietyModalOpen}
                onClose={() => {
                  setVolumeVarietyModalOpen(false);
                  if (skuVolumeData) onSkuChange(calculateVolumeCascade(skuVolumeData, { currentSelection: skuSelection }));
                }}
                title="Select Varieties"
                options={volumeVarietyOptions}
                selectedValues={skuSelection.varieties}
                onSelect={(v) => {
                  const next = (skuSelection.varieties || []).includes(v)
                    ? (skuSelection.varieties || []).filter((x) => x !== v)
                    : [...(skuSelection.varieties || []), v];
                  onSkuChange({ ...skuSelection, varieties: next });
                }}
                multiSelect
                onSelectAll={() => onSkuChange({ ...skuSelection, varieties: [...volumeVarietyOptions] })}
                onClearAll={() => onSkuChange({ ...skuSelection, varieties: [] })}
              />

              <FilterModal
                isOpen={volumeOriginModalOpen}
                onClose={() => {
                  setVolumeOriginModalOpen(false);
                  if (skuVolumeData) onSkuChange(calculateVolumeCascade(skuVolumeData, { currentSelection: skuSelection }));
                }}
                title="Select Origins"
                options={volumeOriginOptions}
                selectedValues={skuSelection.origins}
                onSelect={(v) => {
                  const next = (skuSelection.origins || []).includes(v)
                    ? (skuSelection.origins || []).filter((x) => x !== v)
                    : [...(skuSelection.origins || []), v];
                  onSkuChange({ ...skuSelection, origins: next });
                }}
                multiSelect
                onSelectAll={() => onSkuChange({ ...skuSelection, origins: [...volumeOriginOptions] })}
                onClearAll={() => onSkuChange({ ...skuSelection, origins: [] })}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CustomAlertCommodityConfig;
